/**
 * The three-dimensional arithmetic the shading viewer runs on.
 *
 * Split out of the viewer so the drawing can be tested without a browser: a
 * canvas is easy to fake, a React effect much less so. World frame throughout:
 * x = east, y = north, z = up, metres, azimuths clockwise from north.
 */

export const DEG = Math.PI / 180;

export const v3 = (x, y, z) => ({ x, y, z });
export const add = (a, b) => v3(a.x + b.x, a.y + b.y, a.z + b.z);
export const sub = (a, b) => v3(a.x - b.x, a.y - b.y, a.z - b.z);
export const mul = (a, k) => v3(a.x * k, a.y * k, a.z * k);
export const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
export const cross = (a, b) =>
  v3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
export const norm = (a) => {
  const L = Math.sqrt(dot(a, a)) || 1;
  return v3(a.x / L, a.y / L, a.z / L);
};
export const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Unit vector along a compass azimuth, horizontal. */
export const compass = (azDeg) => v3(Math.sin(azDeg * DEG), Math.cos(azDeg * DEG), 0);

/** Unit vector pointing at the sun. */
export const sunVector = (zenithDeg, azimuthDeg) =>
  v3(
    Math.sin(zenithDeg * DEG) * Math.sin(azimuthDeg * DEG),
    Math.sin(zenithDeg * DEG) * Math.cos(azimuthDeg * DEG),
    Math.cos(zenithDeg * DEG)
  );

/**
 * Where a point's shadow lands on the ground.
 *
 * The ground is the plane z = a*x + b*y, which is flat terrain when the site
 * has no slope and the real tilted plane when it does - so a shadow running
 * downhill lengthens and one running uphill shortens, exactly as on site.
 *
 * Returns null when the sun is in or below the ground plane, where a shadow is
 * not a finite thing. Callers skip those rather than drawing a line to
 * infinity.
 */
/**
 * Is the sun above the ground plane at all?
 *
 * On sloped terrain the horizon is not the horizon: a hillside falling away to
 * the north hides a low southern sun long after it has technically risen, and
 * the whole site sits in the hill's own shadow. The projection below then has
 * no solution - which used to mean the viewer drew NOTHING, so a site in
 * complete shade looked exactly like a site in full sun with its shadows
 * mysteriously missing.
 */
export function sunAboveGround(sun, slopeDir, slopeGrad) {
  const a = slopeDir ? -slopeGrad * slopeDir.x : 0;
  const b = slopeDir ? -slopeGrad * slopeDir.y : 0;
  return sun.z - a * sun.x - b * sun.y > 1e-6;
}

export function shadowPoint(p, sun, slopeDir, slopeGrad) {
  const a = slopeDir ? -slopeGrad * slopeDir.x : 0;
  const b = slopeDir ? -slopeGrad * slopeDir.y : 0;
  const denom = sun.z - a * sun.x - b * sun.y;
  if (Math.abs(denom) < 1e-6) return null;
  const t = (p.z - a * p.x - b * p.y) / denom;
  if (t < 0) return null;
  return sub(p, mul(sun, t));
}
